<template>
    <aside :class="[
        'wysiwyg__options',
        { 'wysiwyg__options--fixed' : isFixed },
        { 'wysiwyg__options--has-inputs' : showPageInputs }
    ]">
        <template v-if="showPageInputs">
            <FormulateInput
                v-model="parentPage"
                name="parentPage"
                class="wysiwyg__dropdown"
                type="select"
                label="Parent Page"
                :options="parentPageOptions"
            />

            <FormulateInput
                v-model="navOrder"
                type="number"
                label="Navigation Order"
                help="Tip: Use increments of 10"
                placeholder="0"
            />
        </template>

        <div class="wysiwyg__actions" v-if="hasCustomButton">
            <slot></slot>
        </div>

        <template v-else>
            <div class="wysiwyg__actions" v-if="isNew || isDraft">
                <Button display="link" @click="$emit('savePage', 'Draft')">Save Draft</Button>
                <Button display="primary" :disabled="loading" @click="$emit('savePage', 'Published')">
                    <span v-if="loading">Publishing</span>
                    <span v-if="!success && !loading">Publish</span>
                    <Spinner v-if="loading" />
                </Button>
            </div>

            <div class="wysiwyg__actions" v-if="!isNew && !isDraft">
                <Button display="link" @click="$emit('savePage', 'Draft')" v-if="!isLegals">
                    Unpublish
                </Button>

                <Button display="primary" :disabled="loading" @click="$emit('savePage', 'Published')">
                    <span v-if="loading">Updating</span>
                    <span v-if="!success && !loading">Update</span>
                    <Spinner v-if="loading" />
                </Button>
            </div>
        </template>
    </aside>
</template>

<script>
export default {
    props: {
        isFixed: {
            type: Boolean
        },
        pages: {
            type: Array
        },
        isPage: {
            type: Boolean,
            default: false
        },
        isArticle: {
            type: Boolean,
            default: false
        },
        isNew: {
            type: Boolean,
            default: false
        },
        isDraft: {
            type: Boolean,
            default: false
        },
        canOnlyUpdate: {
            type: Boolean,
            default: false
        },
        loading: {
            type: Boolean,
            default: false
        },
        success: {
            type: Boolean,
            default: null
        },
        error: {
            type: String,
            default: null
        },
        draftLoading: {
            type: Boolean,
            default: false,
        },
        draftSuccess: {
            type: Boolean,
            default: false
        },
        order: {
            type: [String, Number],
            default: 0
        }
    },

    data() {
        return {
            navOrder: 0,
            parentPage: 'none',
            unwatchParent: true
        }
    },

    async mounted () {
        // we re-assign this since we're using v-model, and don't want to update props
        if (this.order) this.navOrder = this.order

        const { page, child } = this.$route.params

        if (child) {
            const currPage = this.availablePages.filter(({ path }) => path === `/${page}`)[0]
            this.parentPage = `${currPage.path}`
        } else {
            this.parentPage = 'none'
        }

        this.$emit('setOriginalParent', this.parentPage)

        /*
            We have a watcher that passes the selected parent page to the editor.
            If this parent page is different to what's set as 'originalContent', it'll pass
            the value updateSlug: true to the backend API.
            If we don't unwatch this whilst we set the pre-existing page, it'll incorrectly detect
            that we changed parentPage from 'none' to 'currentPath', and set the value to true.
            Defaulting this to true, and turning it off once things are mounted prevents this issue.
        */
        await this.$nextTick()
        this.unwatchParent = false
    },

    computed: {
        availablePages () {
            return this.pages
                .filter(({ hideFromNav }) => !hideFromNav)
                .filter(({ hide_from_nav }) => {
                    // For some reason, the boolean is being passed as a string..?
                    return !hide_from_nav || hide_from_nav == 'false'
                })
                .filter(({ path }) => path !== '/areas')
        },

        parentPageOptions() {
            return this.availablePages
                .reduce((acc, { name, path }) => {
                    if (path.includes('/areas/')) return acc
                    return {...acc, [path]: name }
                }, { none: 'None' })
        },

        hasCustomButton () {
            return this.$slots && this.$slots.default
        },

        isLegals () { return this.$route.name === 'legals' },

        showPageInputs () {
            if (this.isLegals || this.isArticle || !this.isPage) return false
            return true
        }
    },

    watch: {
        navOrder (val) {
            this.$emit('navOrder', val)
        },

        parentPage (page) {
            if (!this.unwatchParent) {
                this.$emit('parentPage', page)
            }
        }
    },

    beforeDestroy () {
        clearTimeout(this.paddingTimer)
    }

}
</script>

